import * as i18n from '@intlify/core'
import EnglishTranslations from './locales/en.json'
import GermanTranslations from './locales/de.json'
import AmericanEnglishTranslations from './locales/us.json'
import { TranslationOverrides } from '../types/CompanyContent'

export const TranslationMessages = {
  en: EnglishTranslations,
  de: GermanTranslations,
  us: AmericanEnglishTranslations
}

export type TranslatorMethod = (key: string, values?: unknown) => string | number

const translate = i18n.translate as (context: unknown, key: string, values?: unknown) => string | number

let TranslatorMethodOverride: TranslatorMethod | undefined
let TranslationContext: ReturnType<typeof i18n.createCoreContext>

/**
 * Sets the current selected locale
 */
export function setSelectedLocale (locale: string = 'en', fallbackLocale: string = 'en'): void {
  if (TranslationContext) {
    TranslationContext.locale = locale
    TranslationContext.fallbackLocale = fallbackLocale
    return
  }
  TranslationContext = i18n.createCoreContext({
    fallbackLocale,
    locale,
    messages: TranslationMessages
  })
}

export function setTranslatorMethod (method: TranslatorMethod): void {
  TranslatorMethodOverride = method
}

/**
 * Sets the given translations map `messages` for the provided `locale`.
 * @param locale
 * @param messages
 */
export function addLocaleMessages (locale: string, messages: any): void {
  TranslationContext.messages = {
    ...TranslationMessages,
    [locale]: messages
  }
}

export const $t: TranslatorMethod = (key: string, values?: unknown): string | number => {
  if (TranslationContext) {
    return translate(TranslationContext, key, values)
  }
  if (!TranslatorMethodOverride) {
    throw new Error('Unable to provide translation as no context or translator override has been provided.')
  }

  if (values === undefined) {
    return TranslatorMethodOverride(key)
  }
  return TranslatorMethodOverride(key, values)
}

/**
 * Creates a translation function for the provided locale `variantCode`.
 * @param variantCode
 */
export const createTranslator = (variantCode: string = 'en'): TranslatorMethod => {
  const context = i18n.createCoreContext({
    fallbackLocale: 'en',
    locale: variantCode,
    messages: TranslationMessages
  })
  return (key: string, values?: unknown): string | number => translate(context, key, values)
}

/**
 * Creates a translation function for the provided locale `variantCode`, with the provided overrides applied.
 */
export const createOverriddenTranslator = (
  variantCode: string = 'en',
  overrides?: TranslationOverrides
): TranslatorMethod => {
  const companyOverrideLocale: string = `${variantCode}-companyOverrides`
  const context = i18n.createCoreContext({
    fallbackLocale: {
      default: ['en']
    },
    locale: companyOverrideLocale,
    messages: {
      ...TranslationMessages,
      [companyOverrideLocale]: overrides ?? {}
    }
  })
  return (key: string, values?: unknown): string | number => translate(context, key, values)
}
